/**
 * Height of the app shell, in CSS pixels — the value behind `--app-height`.
 *
 * In a browser tab the visible area is `window.innerHeight`: it already excludes the URL bar.
 *
 * An installed (standalone) app differs by platform:
 * - iOS standalone can report an `innerHeight` without the bottom system inset, so the shell ended above the
 *   screen edge and a band showed under the app (b8473936). The screen size is not the answer either: with the
 *   `default` status-bar style the app's window starts below the status bar, so the screen height made the shell
 *   one status bar taller than the window and pushed the bottom menu below the screen (QA 21.10, iPhone). The
 *   truth is the height of a `position: fixed; top: 0; bottom: 0` box — the window the page is drawn into.
 * - Android standalone reports `innerHeight` correctly, while `screen.height` includes the status bar and the
 *   gesture/navigation bar. Using the screen there made the shell taller than the visible area and pushed the
 *   bottom menu off-screen — reachable only by scrolling (QA 21.10).
 */
export interface ViewportFacts {
  standalone: boolean;
  ios: boolean;
  innerHeight: number;
  /** Height of a fixed box spanning top to bottom of the window; 0 when it could not be measured. */
  fixedBoxHeight: number;
}

export function appHeight(v: ViewportFacts): number {
  if (!v.standalone || !v.ios) return v.innerHeight;
  return Math.max(v.innerHeight, v.fixedBoxHeight);
}

/** iPhone/iPod, and iPadOS which presents itself as a Mac with touch. */
export function isIos(nav: Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
}
